import React from "react";
import { MapPin } from "lucide-react";

function formatDateISO(d) {
  return d.toISOString().slice(0, 10);
}

export { formatDateISO };

const WEEKDAY_LABELS = [
  { id: "mon", label: "L" },
  { id: "tue", label: "M" },
  { id: "wed", label: "M" },
  { id: "thu", label: "J" },
  { id: "fri", label: "V" },
  { id: "sat", label: "S" },
  { id: "sun", label: "D" },
];

/**
 * Build the day cells for a given month with stable keys.
 * Returns an array of { key, date?, blank?: true }.
 */
function buildMonthCells(year, monthIndex) {
  const startOfMonth = new Date(year, monthIndex, 1);
  const endOfMonth = new Date(year, monthIndex + 1, 0);
  const startDay = (startOfMonth.getDay() + 6) % 7; // Monday first
  const cells = [];
  for (let i = 0; i < startDay; i++) {
    cells.push({ key: `blank-${year}-${monthIndex}-${i}`, blank: true });
  }
  for (let d = 1; d <= endOfMonth.getDate(); d++) {
    const dt = new Date(year, monthIndex, d);
    cells.push({ key: `day-${formatDateISO(dt)}`, date: dt });
  }
  return cells;
}

export default function MiniCalendar({ value, onChange }) {
  const [month, setMonth] = React.useState(() => new Date());
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const cells = React.useMemo(() => buildMonthCells(year, monthIndex), [year, monthIndex]);
  const monthLabel = month.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  const today = React.useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const goPrev = () => setMonth(new Date(year, monthIndex - 1, 1));
  const goNext = () => setMonth(new Date(year, monthIndex + 1, 1));

  return (
    <div className="bg-arven-surface border border-white/10 rounded-2xl p-6 md:p-7 max-w-md">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={goPrev}
          className="text-arven-offwhite/70 hover:text-arven-gold p-2"
          aria-label="Previous month"
        >
          ←
        </button>
        <p className="serif text-xl capitalize">{monthLabel}</p>
        <button
          type="button"
          onClick={goNext}
          className="text-arven-offwhite/70 hover:text-arven-gold p-2"
          aria-label="Next month"
        >
          →
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mt-5 text-center text-[10px] tracking-[0.18em] uppercase text-arven-offwhite/50">
        {WEEKDAY_LABELS.map((w) => (
          <div key={w.id}>{w.label}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1 mt-2">
        {cells.map((cell) => {
          if (cell.blank) return <div key={cell.key} />;
          const d = cell.date;
          const iso = formatDateISO(d);
          const disabled = d < today || d.getDay() === 0;
          const selected = value === iso;
          return (
            <button
              type="button"
              key={cell.key}
              disabled={disabled}
              onClick={() => onChange(iso)}
              data-testid={`cal-day-${iso}`}
              className={`aspect-square text-sm rounded-md transition-all duration-200 ${
                selected
                  ? "bg-arven-gold text-arven-noir font-medium"
                  : disabled
                    ? "text-arven-offwhite/20 cursor-not-allowed"
                    : "text-arven-offwhite/85 hover:bg-white/5"
              }`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Convenience export used by AppointmentPage zones grid
export function ZoneTag({ city }) {
  return (
    <div className="flex items-center gap-2.5 p-4 border border-white/10 rounded-xl bg-arven-noir/40">
      <MapPin size={14} className="text-arven-gold shrink-0" />
      <span className="text-[13px] md:text-sm text-arven-offwhite/85">{city}</span>
    </div>
  );
}
